import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";
import { DollarSign, TrendingUp, Wallet, Percent, FolderKanban } from "lucide-react";

const STATUS = { planning: { es: "Planificación", en: "Planning" }, in_progress: { es: "En progreso", en: "In progress" }, completed: { es: "Completado", en: "Completed" } };

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid={`prof-stat-${label}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{label}</span>
        <Icon className={`h-5 w-5 ${accent || "text-primary"}`} />
      </div>
      <p className="mt-3 font-head text-3xl font-extrabold text-secondary">{value}</p>
    </div>
  );
}

export default function Profitability() {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/projects/profitability").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  return (
    <div>
      <h1 className="font-head text-2xl font-extrabold tracking-tight text-secondary">{t("dash_profitability")}</h1>
      <p className="mt-1 text-sm text-slate-500">{lang === "es" ? "Resumen de ganancias e ingresos reales de todos tus proyectos." : "Summary of real profit and income across all your projects."}</p>

      {!data ? (
        <p className="py-16 text-center text-slate-400">…</p>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
            <Stat icon={FolderKanban} label={lang === "es" ? "Proyectos" : "Projects"} value={data.project_count} />
            <Stat icon={DollarSign} label={lang === "es" ? "Facturado" : "Invoiced"} value={`$${data.total_invoiced.toFixed(0)}`} />
            <Stat icon={Wallet} label={lang === "es" ? "Cobrado" : "Collected"} value={`$${data.total_collected.toFixed(0)}`} accent="text-accent" />
            <Stat icon={TrendingUp} label={lang === "es" ? "Ganancia total" : "Total profit"} value={`$${data.total_profit.toFixed(0)}`} accent={data.total_profit >= 0 ? "text-accent" : "text-destructive"} />
            <Stat icon={Percent} label={lang === "es" ? "Margen prom." : "Avg margin"} value={`${data.avg_margin}%`} />
          </div>

          <div className="mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="profitability-chart">
            <h2 className="font-head text-lg font-bold text-secondary">{lang === "es" ? "Ganancia por proyecto" : "Profit per project"}</h2>
            {data.projects.length === 0 ? (
              <p className="py-10 text-center text-slate-400">—</p>
            ) : (
              <div className="mt-4 h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.projects.map((p) => ({ name: p.title, profit: p.profit }))} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => [`$${Number(v).toFixed(2)}`, lang === "es" ? "Ganancia" : "Profit"]} cursor={{ fill: "rgba(148,163,184,0.1)" }} />
                    <Bar dataKey="profit" radius={[3, 3, 0, 0]}>
                      {data.projects.map((p) => (
                        <Cell key={p.id} fill={p.profit >= 0 ? "#10B981" : "#EF4444"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="mt-6 overflow-x-auto rounded-sm border border-slate-200 bg-white">
            <table className="w-full text-sm" data-testid="profitability-table">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-400">
                  <th className="p-3">{lang === "es" ? "Proyecto" : "Project"}</th>
                  <th className="p-3">{lang === "es" ? "Facturado" : "Invoiced"}</th>
                  <th className="p-3">{lang === "es" ? "Cobrado" : "Collected"}</th>
                  <th className="p-3">{lang === "es" ? "Costo" : "Cost"}</th>
                  <th className="p-3">{lang === "es" ? "Ganancia" : "Profit"}</th>
                  <th className="p-3">{lang === "es" ? "Margen" : "Margin"}</th>
                </tr>
              </thead>
              <tbody>
                {data.projects.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100" data-testid={`prof-row-${p.id}`}>
                    <td className="p-3">
                      <span className="font-semibold text-secondary">{p.title}</span>
                      {p.status && <span className="ml-2 rounded-sm bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">{STATUS[p.status] ? (lang === "es" ? STATUS[p.status].es : STATUS[p.status].en) : p.status}</span>}
                    </td>
                    <td className="p-3 text-slate-600">${p.invoiced_total.toFixed(2)}</td>
                    <td className="p-3 text-accent">${p.collected_total.toFixed(2)}</td>
                    <td className="p-3 text-slate-500">${p.total_cost.toFixed(2)}</td>
                    <td className={`p-3 font-bold ${p.profit >= 0 ? "text-accent" : "text-destructive"}`}>${p.profit.toFixed(2)}</td>
                    <td className="p-3 font-semibold text-secondary">{p.margin}%</td>
                  </tr>
                ))}
                {data.projects.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-slate-400">—</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
